import { useTranslation } from 'react-i18next';

/**
 * The strength bar from `ChangePassword.cshtml`, weights unchanged.
 *
 * This is a hint, not a rule: what actually decides whether a password is
 * accepted is the Identity `PasswordOptions` the server validates against, and
 * the two have never agreed. Kept scoring-identical to the page it replaces so
 * the bar does not start reading differently for the same password.
 */
export function calculatePasswordStrength(password: string): number {
  let strength = 0;

  if (password.length >= 8) strength += 20;
  if (password.length >= 12) strength += 10;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 20;
  if (/\d/.test(password)) strength += 20;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 20;
  if (password.length >= 16) strength += 10;

  return Math.min(strength, 100);
}

export function PasswordStrengthMeter({ password }: { password: string }) {
  const { t } = useTranslation();

  const strength = calculatePasswordStrength(password);
  const empty = password.length === 0;

  // Thresholds and colours from the original updatePasswordStrengthIndicator.
  const band = strength < 40 ? 'danger' : strength < 70 ? 'warning' : strength < 90 ? 'info' : 'success';
  const label = {
    danger: t('auth:Auth.Weak', 'Weak'),
    warning: t('auth:Auth.Fair', 'Fair'),
    info: t('auth:Auth.Good', 'Good'),
    success: t('auth:Auth.Strong', 'Strong'),
  }[band];

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center">
        <span className="small text-muted me-2">{t('auth:Auth.Strength', 'Strength')}</span>
        <div className="progress flex-grow-1" style={{ height: 8 }}>
          <div
            className={`progress-bar${empty ? '' : ` bg-${band}`}`}
            role="progressbar"
            style={{ width: empty ? '0%' : `${strength}%` }}
            aria-valuenow={empty ? 0 : strength}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t('auth:Auth.Strength', 'Strength')}
          />
        </div>
        <span className={`small ms-2 ${empty ? 'text-muted' : `text-${band}`}`}>{empty ? '' : label}</span>
      </div>
    </div>
  );
}
